import type React from 'react';

import { StackedChildren } from '@zillow/zrs-comet';

import { SECURITY_DEPOSIT } from '../../../constants/index';
import { FEE_PAYMENT_TIME } from '../../../constants/stringConstants';
import type { Section } from '../../../models/index';
import SecurityDepositBankAddressInput from './SecurityDeposit/SecurityDepositBankAddressInput';
import SecurityDepositBankNameInput from './SecurityDeposit/SecurityDepositBankNameInput';
import SecurityDepositCleaningCostYesAndNo from './SecurityDeposit/SecurityDepositCleaningCostYesAndNo';
import SecurityDepositCurrencyInput from './SecurityDeposit/SecurityDepositCurrencyInput';
import SecurityDepositInterestYesAndNo from './SecurityDeposit/SecurityDepositInterestYesAndNo';
import SecurityDepositYesAndNoExpansion from './SecurityDeposit/SecurityDepositYesAndNoExpansion';
import FeePaymentTime_v2 from '../../Widgets/FeePaymentTime_v2';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SecurityDeposit_v9({ section, setSectionValues }: Props): JSX.Element {
  function disableSecurityDeposit(): void {
    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: false });
  }

  function enableSecurityDeposit(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: true,
      [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: value });
  }

  function onBankNameChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.BANK_NAME]: value });
  }

  function onBankAddressChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.BANK_ADDRESS]: value });
  }

  function onPayDueClick(event: any): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
      [SECURITY_DEPOSIT.FIELD_NAME.TWO_EQUAL_INSTALLMENTS]: id === FEE_PAYMENT_TIME.TWO_EQUAL_INSTALLMENTS,
      [SECURITY_DEPOSIT.FIELD_NAME.THREE_EQUAL_INSTALLMENTS]: id === FEE_PAYMENT_TIME.THREE_EQUAL_INSTALLMENTS,
    });
  }

  function onCleaningCostYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: true,
    });
  }

  function onCleaningCostNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST]: false,
    });
  }

  function onInterestYesClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: true,
    });
  }

  function onInterestNoClick(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.INTEREST]: false,
    });
  }

  const cleaningCostFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST);
  const interestFieldValue = section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.INTEREST);

  return (
    <SecurityDepositYesAndNoExpansion
      yesHandler={enableSecurityDeposit}
      noHandler={disableSecurityDeposit}
      yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
      disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
    >
      <StackedChildren size="sm">
        <SecurityDepositCurrencyInput onBlur={onAmountChange} onChange={onAmountChange} section={section} />
        <SecurityDepositBankNameInput
          value={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.BANK_NAME)}
          onChange={onBankNameChange}
        />
        <SecurityDepositBankAddressInput
          value={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.BANK_ADDRESS)}
          onChange={onBankAddressChange}
          section={section}
        />
        <FeePaymentTime_v2
          label="When is the security deposit due?"
          section={section}
          onChange={onPayDueClick}
          firstChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING)}
          secondChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE)}
          thirdChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.TWO_EQUAL_INSTALLMENTS)}
          fourthChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.THREE_EQUAL_INSTALLMENTS)}
        />
        <SecurityDepositCleaningCostYesAndNo
          yesHandler={onCleaningCostYesClick}
          noHandler={onCleaningCostNoClick}
          yesActive={cleaningCostFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.CLEANING_COST)}
        />
        <SecurityDepositInterestYesAndNo
          yesHandler={onInterestYesClick}
          noHandler={onInterestNoClick}
          yesActive={interestFieldValue}
          disabled={section.isFieldDisabled(SECURITY_DEPOSIT.FIELD_NAME.INTEREST)}
        />
      </StackedChildren>
    </SecurityDepositYesAndNoExpansion>
  );
}

SecurityDeposit_v9.description = 'WA Specific security deposit';
